/**
 * QuotaUsageCard - Display current quota usage and limits
 *
 * **Supports Universal Quota Architecture (2025-11-25)**
 *
 * This component accepts both the new universal quota props (`quotaUsed`, `quotaLimit`)
 * and legacy props (`dailyAnalysesUsed`, `dailyAnalysesLimit`) for backward compatibility.
 *
 * ## Migration Guide
 *
 * ```typescript
 * // BEFORE (legacy props)
 * <QuotaUsageCard
 *   dailyAnalysesUsed={quotaData.dailyAnalysesUsed}
 *   dailyAnalysesLimit={quotaData.dailyAnalysesLimit}
 *   // ...
 * />
 *
 * // AFTER (universal props - preferred)
 * <QuotaUsageCard
 *   quotaUsed={quotaData.used}
 *   quotaLimit={quotaData.limit}
 *   quotaRemaining={quotaData.remaining}
 *   resetPeriod={quotaData.resetPeriod}
 *   quotaSource={quotaData.source}
 *   // ...
 * />
 * ```
 *
 * Features:
 * - Progress bar with color-coded usage
 * - Usage text with remaining count
 * - Live countdown timer (updates every second)
 * - Tier badge display
 * - Upgrade CTA for free tier
 * - Loading and error states
 * - Automatic quota reset via Realtime subscription
 * - Reset period awareness (daily vs weekly)
 * - Quota source tracking (anonymous, individual, family)
 *
 * @see useUniversalQuota for the unified quota hook
 * @see /packages/drama-detector-docs/features/universal-quota-architecture.md
 */
import { View, Text, Pressable } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { useCountdown } from '../../hooks/useCountdown';

interface QuotaUsageCardProps {
  // === Universal Quota Fields (NEW - preferred) ===
  /** Number of analyses used in current period (universal format) */
  quotaUsed?: number;
  /** Maximum analyses allowed in current period (universal format) */
  quotaLimit?: number;
  /** Analyses remaining in current period (universal format) */
  quotaRemaining?: number;
  /** Reset period: 'daily' for authenticated users, 'weekly' for anonymous */
  resetPeriod?: 'daily' | 'weekly';
  /** Quota source: 'anonymous', 'individual', or 'family' (shared quota) */
  quotaSource?: 'anonymous' | 'individual' | 'family';

  // === Legacy Fields (DEPRECATED - backward compatibility) ===
  /** @deprecated Use `quotaUsed` instead */
  dailyAnalysesUsed?: number;
  /** @deprecated Use `quotaLimit` instead */
  dailyAnalysesLimit?: number;

  // === Unchanged Fields ===
  usagePercent: number;
  remaining: number;
  resetTime: string; // Formatted human-readable time (deprecated - use resetAt)
  resetAt?: string; // ISO 8601 timestamp for live countdown
  currentTier: 'free' | 'drama_plus' | 'dramax' | 'anonymous';
  onUpgradeClick?: () => void;
  onRefresh?: () => void; // Refresh quota data (error state retry)
  isLoading?: boolean;
  error?: string | null;
}

/**
 * Get progress bar color based on usage percentage
 */
function getProgressColor(percent: number): string {
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 80) return 'bg-orange-500';
  if (percent >= 50) return 'bg-yellow-500';
  return 'bg-green-500';
}

/**
 * Get text color based on usage percentage
 */
function getTextColor(percent: number): string {
  if (percent >= 90) return 'text-red-700';
  if (percent >= 80) return 'text-orange-700';
  if (percent >= 50) return 'text-yellow-700';
  return 'text-green-700';
}

/**
 * Get tier badge emoji
 */
function getTierEmoji(tier: string): string {
  switch (tier) {
    case 'drama_plus':
      return '💎';
    case 'dramax':
      return '👑';
    case 'anonymous':
      return '🔒';
    case 'free':
      return '🆓';
    default:
      return '🆓';
  }
}

export function QuotaUsageCard({
  // Universal quota props (new)
  quotaUsed,
  quotaLimit,
  quotaRemaining,
  resetPeriod,
  quotaSource,
  // Legacy props (deprecated)
  dailyAnalysesUsed,
  dailyAnalysesLimit,
  // Unchanged props
  usagePercent,
  remaining,
  resetTime,
  resetAt,
  currentTier,
  onUpgradeClick,
  onRefresh,
  isLoading = false,
  error = null,
}: QuotaUsageCardProps) {
  const { t } = useTranslation('subscription');

  // Resolve to universal format (prefer new props, fall back to legacy)
  const resolvedUsed = quotaUsed ?? dailyAnalysesUsed ?? 0;
  const resolvedLimit = quotaLimit ?? dailyAnalysesLimit ?? 0;
  const resolvedRemaining = quotaRemaining ?? remaining ?? 0;
  const resolvedPeriod = resetPeriod ?? (currentTier === 'anonymous' ? 'weekly' : 'daily');
  const resolvedSource = quotaSource ?? (currentTier === 'anonymous' ? 'anonymous' : 'individual');

  const tierEmoji = getTierEmoji(currentTier);
  const progressColor = getProgressColor(usagePercent);
  const textColor = getTextColor(usagePercent);

  // Live countdown timer (updates every second)
  const countdown = useCountdown(resetAt || null);

  /**
   * Format countdown display with time
   * Examples:
   * - "Resets in about 20 hours (6:34 PM GMT-5)"
   * - "Resets in about 1 hour (2:15 PM GMT-5)"
   * - "Resets in 45 minutes (1:30 PM GMT-5)"
   * - "✅ Quota has reset!" (expired)
   */
  const getCountdownDisplay = () => {
    if (!resetAt) {
      // Fallback to static formatted time if no ISO timestamp provided
      return resetTime;
    }

    if (countdown.isExpired) {
      // FIX: Show passive "Updating..." message instead of prompting user to refresh
      // Quota reset now happens automatically via Realtime subscription
      return t('quota.reset.updating');
    }

    // Format the exact reset time
    const resetDate = new Date(resetAt);
    const timeString = resetDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });

    // Format relative time (less distracting)
    let relativeTime: string;
    if (countdown.hours > 1) {
      relativeTime = t('quota.reset.relative.about_hours', { count: countdown.hours });
    } else if (countdown.hours === 1) {
      relativeTime = t('quota.reset.relative.about_hours_one');
    } else if (countdown.minutes > 1) {
      relativeTime = t('quota.reset.relative.minutes', { count: countdown.minutes });
    } else {
      relativeTime = t('quota.reset.relative.less_than_minute');
    }

    return `${t('quota.reset.in', { time: relativeTime })} (${timeString})`;
  };

  // Loading state
  if (isLoading) {
    return (
      <View className="bg-white rounded-lg p-4 mb-4 border border-gray-200" data-testid="quota-usage-card">
        <View className="flex-row items-center mb-3">
          <Text className="text-lg font-semibold text-gray-900">📊 {t('quota.title')}</Text>
        </View>
        <View className="h-4 bg-gray-200 rounded mb-2 animate-pulse" />
        <View className="h-4 bg-gray-200 rounded w-2/3 mb-2 animate-pulse" />
        <View className="h-4 bg-gray-200 rounded w-1/2 animate-pulse" />
      </View>
    );
  }

  // Error state
  if (error) {
    return (
      <View className="bg-white rounded-lg p-4 mb-4 border border-red-200" data-testid="quota-usage-card">
        <View className="flex-row items-center mb-2">
          <Text className="text-lg font-semibold text-gray-900">📊 {t('quota.title')}</Text>
        </View>
        <Text className="text-sm text-red-600 mb-2">
          {t('quota.errors.load_failed', { message: error })}
        </Text>
        <Pressable
          onPress={onRefresh}
          disabled={!onRefresh}
          className="bg-blue-50 py-2 px-4 rounded active:bg-blue-100 disabled:opacity-50"
          accessibilityRole="button"
          accessibilityLabel={t('quota.errors.retry')}
        >
          <Text className="text-blue-600 text-sm font-medium text-center">{t('quota.errors.retry')}</Text>
        </Pressable>
      </View>
    );
  }

  // Anonymous user view (Weekly Quota)
  if (currentTier === 'anonymous') {
    // Monday countdown for anonymous users
    const getMondayCountdown = () => {
      if (!resetAt) {
        return t('quota.reset.monday');
      }

      if (countdown.isExpired) {
        return t('quota.reset.updating');
      }

      // Show days + hours for weekly countdown
      if (countdown.days > 1) {
        return t('quota.reset.in', { time: t('quota.reset.relative.days', { count: countdown.days }) });
      } else if (countdown.days === 1) {
        return t('quota.reset.in', { time: t('quota.reset.relative.one_day') });
      } else if (countdown.hours > 1) {
        return t('quota.reset.in', { time: t('quota.reset.relative.about_hours', { count: countdown.hours }) });
      } else if (countdown.hours === 1) {
        return t('quota.reset.in', { time: t('quota.reset.relative.about_hours_one') });
      } else if (countdown.minutes > 1) {
        return t('quota.reset.in', { time: t('quota.reset.relative.minutes', { count: countdown.minutes }) });
      } else {
        return t('quota.reset.in', { time: t('quota.reset.relative.less_than_minute') });
      }
    };

    return (
      <View className="bg-white rounded-lg p-4 mb-4 border border-gray-200" data-testid="quota-usage-card">
        <View className="flex-row items-center mb-3">
          <Text className="text-lg font-semibold text-gray-900">📊 {t('quota.weekly_analyses')}</Text>
        </View>
        <Text className={`text-sm font-medium mb-2 ${textColor}`}>
          {t('quota.usage', { used: resolvedUsed, limit: resolvedLimit, remaining: resolvedRemaining })}
        </Text>

        {/* Progress Bar */}
        <View className="w-full h-2 bg-gray-200 rounded-full mb-3 overflow-hidden">
          <View
            className={`h-full ${progressColor}`}
            style={{ width: `${Math.min(usagePercent, 100)}%` }}
          />
        </View>

        {/* Monday Reset Time */}
        {(resetTime || resetAt) && (
          <Text
            className={`text-xs mb-2 ${
              countdown.isExpired ? 'text-green-600 font-medium' : 'text-gray-500'
            }`}
          >
            🔄 {getMondayCountdown()}
          </Text>
        )}

        {onUpgradeClick && (
          <Pressable
            onPress={onUpgradeClick}
            className="bg-blue-50 py-3 px-4 rounded active:bg-blue-100 mt-2"
            style={{ minHeight: 44 }}
            accessibilityRole="button"
            accessibilityLabel={t('quota.actions.sign_up')}
          >
            <Text className="text-blue-600 text-sm font-medium text-center">
              🔒 {t('quota.actions.sign_up')} →
            </Text>
          </Pressable>
        )}
      </View>
    );
  }

  // Authenticated user view
  return (
    <View className="bg-white rounded-lg p-4 mb-4 border border-gray-200" data-testid="quota-usage-card">
      {/* Header */}
      <View className="flex-row items-center justify-between mb-3">
        <Text className="text-lg font-semibold text-gray-900">📊 {t('quota.daily_analyses')}</Text>
        <View className="flex-row items-center" data-testid="tier-badge">
          <Text className="text-base">{tierEmoji}</Text>
          <Text className="text-sm text-gray-600 ml-1">{t(`quota.tiers.${currentTier}`)}</Text>
        </View>
      </View>

      {/* Usage Text */}
      <Text className={`text-sm font-medium mb-2 ${textColor}`}>
        {t('quota.usage', { used: resolvedUsed, limit: resolvedLimit, remaining: resolvedRemaining })}
      </Text>

      {/* Progress Bar */}
      <View className="w-full h-2 bg-gray-200 rounded-full mb-3 overflow-hidden">
        <View
          className={`h-full ${progressColor}`}
          style={{ width: `${Math.min(usagePercent, 100)}%` }}
        />
      </View>

      {/* Reset Time - Live Countdown */}
      {(resetTime || resetAt) && (
        <Text
          className={`text-xs mb-2 ${
            countdown.isExpired ? 'text-green-600 font-medium' : 'text-gray-500'
          }`}
        >
          🔄 {getCountdownDisplay()}
        </Text>
      )}

      {/* Upgrade CTA (only for free tier) */}
      {currentTier === 'free' && onUpgradeClick && (
        <Pressable
          onPress={onUpgradeClick}
          className="bg-blue-50 py-3 px-4 rounded active:bg-blue-100 mt-2"
          style={{ minHeight: 44 }}
          accessibilityRole="button"
          accessibilityLabel={t('quota.actions.upgrade', { tier: 'Drama+', quota: '100' })}
        >
          <Text className="text-blue-600 text-sm font-medium text-center">
            💎 {t('quota.actions.upgrade', { tier: 'Drama+', quota: '100' })} →
          </Text>
        </Pressable>
      )}
    </View>
  );
}
